import {
  Delete as DeleteIcon,
  Edit as EditIcon} from '@mui/icons-material';
import {
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import ExpandableRow from '@/components/ui/ExpandableRow';
import usePlaylistData from '@/hooks/usePlaylistData';
import { Playlist } from '@/lib/api/playlist';
import { Program } from '@/lib/api/program';
import { formatTimeWithMilliseconds, formatUuidLastPartWithMimeType, getStatusText } from '@/lib/utils/formatters';

interface ExpandableTableRowProps {
  program: Program;
  formatDate: (dateString: string) => string;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
}

/**
 * Expandable program table row component
 * Displays a program row and shows related playlists when clicked
 */
export const ExpandableTableRow: React.FC<ExpandableTableRowProps> = ({
  program,
  formatDate,
  onEdit,
  onDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Flag to track if the initial playlist fetch has been triggered for this row
  const [dataFetched, setDataFetched] = useState(false);
  // Flag to prevent further fetches if the program is confirmed to have no playlists
  const [programConfirmedEmpty, setProgramConfirmedEmpty] = useState(false);

  // Only fetch playlist data when expanded (no limit)
  const {
    playlists,
    loading,
    error,
  } = usePlaylistData(
    // Fetch playlists only if the row is expanded (dataFetched is true)
    // and if it hasn't been confirmed that this program has no playlists.
    dataFetched && !programConfirmedEmpty ? program.id : undefined,
    undefined, // No limit, fetch all
    0,
    true, // Enable silent update mode
  );

  useEffect(() => {
    // If the fetch completes (dataFetched is true, loading is false)
    // and returns no playlists, mark this program as confirmed empty
    // to prevent future fetches when the row is toggled.
    if (dataFetched && !loading && playlists.length === 0) {
      setProgramConfirmedEmpty(true);
    }
    // Reset dataFetched flag when the row is closed,
    // so that fetch is triggered again if reopened (unless confirmed empty).
    if (!isOpen) {
      setDataFetched(false);
      // Do not reset programConfirmedEmpty.
    }
  }, [isOpen, dataFetched, loading, playlists]);

  // Main cells for the program row
  const programCells = [
    program.id,
    formatDate(program.since),
    formatDate(program.until),
    program.duration,
    program.supplier || '-',
  ];

  // Action cell (edit and delete buttons)
  const actionCell = (
    <TableCell align="right">
      <Tooltip title="編集">
        <IconButton
          aria-label="edit"
          color="primary"
          onClick={(e) => {
            e.stopPropagation(); // Stop click event propagation
            onEdit(program.id);
          }}
          size="small"
        >
          <EditIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="削除">
        <IconButton
          aria-label="delete"
          color="error"
          onClick={(e) => {
            e.stopPropagation(); // Stop click event propagation
            onDelete(program.id);
          }}
          size="small"
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    </TableCell>
  );

  // Content to display when expanded
  const expandedContent = (
    <>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      <Table size="small" aria-label="プレイリスト">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>ファイル名</TableCell>
            <TableCell>ステータス</TableCell>
            <TableCell>開始時刻</TableCell>
            <TableCell>Start</TableCell>
            <TableCell>Quarter</TableCell>
            <TableCell>Half</TableCell>
            <TableCell>3/4</TableCell>
            <TableCell>Complete</TableCell>
            <TableCell>Error</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={10} align="center">
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 1, display: 'inline' }}>
                  読み込み中...
                </Typography>
              </TableCell>
            </TableRow>
          ) : playlists.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center">
                プレイリストが見つかりません
              </TableCell>
            </TableRow>
          ) : (
            playlists.map((playlist: Playlist) => (
              <TableRow key={playlist.id}>
                <TableCell>{playlist.id}</TableCell>
                <TableCell>
                  <Tooltip title={playlist.src} arrow>
                    <span>{formatUuidLastPartWithMimeType(playlist.src, playlist.mimeType)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>{getStatusText(playlist.status)}</TableCell>
                <TableCell>{formatTimeWithMilliseconds(playlist.since)}</TableCell>
                <TableCell>{formatTimeWithMilliseconds(playlist.statsStartAt)}</TableCell>
                <TableCell>{formatTimeWithMilliseconds(playlist.statsQuarterAt)}</TableCell>
                <TableCell>{formatTimeWithMilliseconds(playlist.statsHalfAt)}</TableCell>
                <TableCell>{formatTimeWithMilliseconds(playlist.statsThreeQuartersAt)}</TableCell>
                <TableCell>{formatTimeWithMilliseconds(playlist.statsCompleteAt)}</TableCell>
                <TableCell>{formatTimeWithMilliseconds(playlist.statsErrorAt)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </>
  );

  // Toggle open state and start fetching data when row is expanded
  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    if (newState && !dataFetched) {
      setDataFetched(true);
    }
  };

  return (
    <ExpandableRow
      id={program.id}
      cells={programCells}
      expandedContent={expandedContent}
      colSpan={7}
      open={isOpen}
      onToggle={handleToggle}
      actionCell={actionCell}
    />
  );
};

export default ExpandableTableRow;
